import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  CellDoubleClickedEvent,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowNode,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid';
import { ValueGetterParams } from 'ag-grid/dist/lib/entities/colDef';
import { constant } from 'app/app.constants';
import { AgGridSortFilterUtils } from 'app/core/ag-grid-utils/ag-grid-sort-filter-utils';
import {
  AgButtonsEnum,
  DepositAction,
  ElectronicGridEnum,
  TabsElectronicEnum,
} from 'app/core/enums/tabs-deposits.enum';
import { CustomDate } from 'app/core/helpers/format-date-helper';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { IDenialReason } from 'app/core/interfaces/deposit.interface';
import { ConciliationService } from 'app/core/services/conciliation.service';
import { T2CardComponent } from 'app/trip2-theme/components/t2-card/t2-card.component';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { _currencyFormat } from 'app/utils/trip2-query-builder/utils/helpers';
import { Subscription } from 'rxjs/Subscription';

@Component({
  selector: 'app-electronic-details',
  templateUrl: './electronic-details.component.html',
  styleUrls: ['./electronic-details.component.scss'],
})
export class ElectronicDetailsComponent implements OnDestroy, OnChanges {
  @ViewChild('denialReasonsModal') private denialReasonsModal: T2CardComponent;
  @Input() electronicId: number;
  tabsElectronic = constant.DEPOSITS_POSTING.TABS_ELECTRONIC;
  activatedTab = TabsElectronicEnum.Claims;
  checkId: number;
  countSelected = 0;
  totalBilledDetail = 0;
  totalAllowedDetail = 0;
  totalDeductDetail = 0;
  totalCoinsDetail = 0;
  totalPaidDetail = 0;
  totalRowsClaimsGrid = 0;
  totalAmountSelected = 0;
  selectedLowerGridOptRows = 0;
  totalRowsLowerGrid = 0;
  totalBilledDetails = 0;
  totaPaidDetails = 0;

  selectedProceduteClaimDetail: string;
  selectedIcnClaimDetail: string;
  totalAllowedAmount: string;
  totalBilledAmount: string;
  totalCheckeftAmount: string;
  totalCoinsuranceAmount: string;
  totalDeductibleAmount: string;
  totalInterestAmount: string;
  totalPaidToProvider: string;
  totalReasonAdjustmentAmount: string;

  disableClearFilterUpperGridOpt = true;
  disableClearSortUpperGridOpt = true;
  disableClearFilterLowerGridOpt = true;
  disableClearSortLowerGridOpt = true;
  disableClearFilterClaimDetailGrid = true;
  disableClearSortClaimDetailGrid = true;

  remitSummary = false;

  claimDetailGrid: GridOptions;
  lowerGridOpt: GridOptions;
  remitGrid: GridOptions;
  denialReasonsGrid: GridOptions;

  subscriptions: Subscription[] = [];

  headerDataSelected = {};

  checkDetail: any;
  lastCheckDetail: any;
  firstDetail: any;
  electronicProviderAdjustment: any;

  readonly agGridButtonActionList = {
    RemoveFilter: AgButtonsEnum.RemoveFilter,
    RemoveSort: AgButtonsEnum.RemoveSort,
  };

  readonly agGridElectronic = {
    UpperGrid: ElectronicGridEnum.UpperGridOpt,
    LowerGrid: ElectronicGridEnum.LowerGridOpt,
    ClaimDetailsGrid: ElectronicGridEnum.ClaimDetailGrid,
  };

  constructor(
    protected objServ: ConciliationService,
    private sweetAlertHelper: SweetAlertHelper,
    private t2LoadingService: T2LoadingService
  ) {
    this.initObservables();
    this.initGrid();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes['electronicId'] &&
      changes['electronicId'].currentValue
    ) {
      this.activatedTab = TabsElectronicEnum.Claims;
      this.getElectronicDetails(this.electronicId);
      this.getRemitSumary(this.electronicId);
    }
  }

  initObservables() {
    this.subscriptions.push(
      this.objServ.resetElectronic$.subscribe({
        next: (value) => {
          if (value) {
            this.clearComponent();
          }
        },
      }),

      this.objServ.claimsCount$.subscribe({
        next: (value) => {
          if (value) {
            this.countSelected = value;
          }
        },
      })
    );
  }

  clearComponent() {
    this.onChangeTabs(this.tabsElectronic.Claim);
    this.remitSummary = false;
    this.totalRowsLowerGrid = 0;
    this.totalBilledDetails = 0;
    this.totaPaidDetails = 0;
    this.selectedLowerGridOptRows = 0;
    if (this.lowerGridOpt && this.lowerGridOpt.api) {
      this.lowerGridOpt.api.setRowData([]);
    }
  }

  getElectronicDetails(electronicId: number) {
    this.t2LoadingService.show();
    this.subscriptions.push(
      this.objServ
        .getElectronicDetailList(electronicId)
        .finally(() => this.t2LoadingService.hiden())
        .subscribe({
          next: (res) => {
            if (res && res.length) {
              this.totalRowsLowerGrid = res.length;
              this.totalBilledDetails = this.calculateTotalAmount(
                res,
                'billedamount'
              );
              this.totaPaidDetails = this.calculateTotalAmount(
                res,
                'paidamount'
              );
              if (this.lowerGridOpt && this.lowerGridOpt.api) {
                this.lowerGridOpt.api.setRowData(res);
                this.lowerGridOpt.api.sizeColumnsToFit();
              }
            }
          },
          error: (err) => this.sweetAlertHelper.captureException(err),
        })
    );
  }

  getRemitSumary(electronicId: number) {
    this.remitSummary = true;
    this.subscriptions.push(
      this.objServ.getRemitSummaryById(electronicId).subscribe({
        next: (value) => {
          if (value && value.electronic) {
            this.headerDataSelected = value.electronic;
          }

          if (value && value.remitsummaryresponse) {
            this.totalAllowedAmount = _currencyFormat(
              value.remitsummaryresponse.totalallowedamount
            );
            this.totalBilledAmount = _currencyFormat(
              value.remitsummaryresponse.totalbilledamount
            );
            this.totalCheckeftAmount = _currencyFormat(
              value.remitsummaryresponse.totalcheckeftamount
            );
            this.totalCoinsuranceAmount = _currencyFormat(
              value.remitsummaryresponse.totalcoinsuranceamount
            );
            this.totalDeductibleAmount = _currencyFormat(
              value.remitsummaryresponse.totaldeductibleamount
            );
            this.totalInterestAmount = _currencyFormat(
              value.remitsummaryresponse.totalinterestamount
            );
            this.totalPaidToProvider = _currencyFormat(
              value.remitsummaryresponse.totalpaidtoprovider
            );
            this.totalReasonAdjustmentAmount = _currencyFormat(
              value.remitsummaryresponse.totalreasonadjustmentamount
            );
            this.electronicProviderAdjustment =
              value.remitsummaryresponse.electronicprovideradjustment;
            this.remitGrid.api.setRowData(this.electronicProviderAdjustment);
            this.remitGrid.api.sizeColumnsToFit();
          }
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        },
      })
    );
  }

  onChangeTabs(selectedTab: TabsElectronicEnum): void {
    switch (selectedTab) {
      case TabsElectronicEnum.Claims:
        this.totalBilledDetail = 0;
        this.totalAllowedDetail = 0;
        this.totalDeductDetail = 0;
        this.totalCoinsDetail = 0;
        this.totalPaidDetail = 0;
        this.totalRowsClaimsGrid = 0;
        this.activatedTab = selectedTab;
        break;
      case TabsElectronicEnum.ClaimsDetail:
        this.activatedTab = selectedTab;
        break;
      case TabsElectronicEnum.RemitSummary:
        this.activatedTab = selectedTab;
        break;
      default:
        break;
    }
  }

  initGrid() {
    this.claimDetailGrid = <GridOptions>{
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      columnDefs: [
        {
          headerName: 'NAME',
          field: 'firstname',
          width: 150,
          cellRenderer: (params) => {
            if (
              params &&
              params.data &&
              params.data.lastname &&
              params.data.firstname
            ) {
              return `${params.data.firstname} ${params.data.lastname}`;
            }
          },
        },
        {
          headerName: 'DATE',
          field: 'fromdate',
          width: 100,
          cellRenderer: (params) => {
            if (params && params.data && params.data.fromdate) {
              return CustomDate.formatJs(params.data.fromdate);
            }
          },
        },
        {
          headerName: 'ICN',
          field: 'icn',
          width: 120,
        },
        {
          headerName: 'HIC',
          field: 'hci',
          width: 100,
        },
        {
          headerName: 'PROCEDURE',
          field: 'procedure',
          width: 100,
        },
        {
          headerName: 'ACNT',
          field: 'acnt',
          width: 100,
        },
        {
          headerName: 'MILES',
          field: 'totalmiles',
          width: 80,
        },
        {
          headerName: 'BILLED',
          field: 'billedamount',
          width: 100,
          cellRenderer: (params) => {
            if (params && params.data && params.data.billedamount) {
              return _currencyFormat(params.data.billedamount);
            }
          },
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'ALLOWED',
          field: 'totalallowedamount',
          width: 100,
          cellRenderer: (params) => {
            return _currencyFormat(params.data.totalallowedamount);
          },
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'DEDUCT',
          field: 'deductibleamount',
          width: 100,
          cellRenderer: (params) => {
            return _currencyFormat(params.data.deductibleamount);
          },
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'COINS',
          field: 'coinsuranceamount',
          width: 100,
          cellRenderer: (params) => {
            return _currencyFormat(params.data.coinsuranceamount);
          },
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'PROV-PD',
          field: 'paidamount',
          width: 100,
          cellRenderer: (params) => {
            return _currencyFormat(params.data.paidamount);
          },
          cellStyle: { 'text-align': 'end' },
        },
        {
          headerName: 'CODES',
          field: 'denialreasons',
          width: 100,
          valueGetter: (params) =>
            this.renderAndGetterValuesForDenialReasons(params),
          cellRenderer: (params) =>
            this.renderAndGetterValuesForDenialReasons(params),
        },
      ],
      onGridReady: (event: GridReadyEvent) => {
        if (event && this.claimDetailGrid.api) {
          this.claimDetailGrid.api.setRowData([]);
          this.claimDetailGrid.api.sizeColumnsToFit();
        }
      },
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const { data } = event;
        const {
          procedure,
          icn,
          denialreasons,
        }: { procedure: string; icn: string; denialreasons: IDenialReason[] } =
          data;

        this.selectedProceduteClaimDetail = procedure;
        this.selectedIcnClaimDetail = icn;

        this.objServ.behaviorSubjDeposit.next({
          action: DepositAction.ShowDenialReasonsAfterDoubleClick,
          data: denialreasons,
        });
        this.denialReasonsModal.open();
      },
      onFilterChanged: ({ api }: FilterChangedEvent) =>
        this.onFilterChangedGrid(api, ElectronicGridEnum.ClaimDetailGrid),
      onSortChanged: ({ api }: SortChangedEvent) =>
        this.updateClearAndFilterBtn(api, ElectronicGridEnum.ClaimDetailGrid),
    };

    this.remitGrid = <GridOptions>{
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      columnDefs: [
        {
          headerName: 'REASON',
          field: 'provideradjustmentreason',
          width: 200,
        },
        {
          headerName: 'FCN/ OTHER IDENTIFIER',
          field: 'provideradjustmentidentifier',
          width: 370,
        },
        {
          headerName: 'AMOUNT',
          field: 'provideradjustmentamount',
          width: 240,
          cellRenderer: (params) => {
            return _currencyFormat(params.data.provideradjustmentamount);
          },
          cellStyle: { 'text-align': 'end' },
        },
      ],
      onGridReady: (event: GridReadyEvent) => {
        if (event && this.remitGrid.api) {
          this.remitGrid.api.setRowData([]);
        }
      },
    };

    this.lowerGridOpt = <GridOptions>{
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      columnDefs: [
        {
          checkboxSelection: true,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          colId: 'electronicid',
          width: 50,
        },
        {
          headerName: 'First Name',
          field: 'firstname',
          width: 150,
        },
        {
          headerName: 'Last Name',
          field: 'lastname',
          width: 150,
        },
        {
          headerName: 'ACNT',
          field: 'acnt',
          width: 150,
        },
        {
          headerName: 'ICN',
          field: 'icn',
          width: 150,
        },
        {
          headerName: 'Billed Amt.',
          field: 'billedamount',
          cellStyle: { 'text-align': 'end' },
          width: 120,
          cellRenderer: (params) => {
            return _currencyFormat(params.data.billedamount);
          },
          filterParams: AgGridSortFilterUtils.sortFilterColumn,
        },
        {
          headerName: 'Paid Amt.',
          field: 'paidamount',
          cellStyle: { 'text-align': 'end' },
          width: 120,
          cellRenderer: (params) => {
            return _currencyFormat(params.data.paidamount);
          },
          filterParams: AgGridSortFilterUtils.sortFilterColumn,
        },
        {
          headerName: 'From Date',
          field: 'fromdate',
          width: 100,
          valueGetter: (params) => {
            if (params && params.data.fromdate) {
              return CustomDate.formatJs(params.data.fromdate);
            }
          },
          cellStyle: { 'text-align': 'center' },
        },
        {
          headerName: 'To Date',
          field: 'todate',
          width: 100,
          valueGetter: (params) => {
            if (params && params.data.todate) {
              return CustomDate.formatJs(params.data.todate);
            }
          },
          cellStyle: { 'text-align': 'center' },
        },
        {
          headerName: 'ASG',
          field: 'asg',
          width: 100,
          hide: true,
        },
        {
          headerName: 'Status',
          field: 'isactive',
          width: 100,
          hide: true,
        },
      ],
      onGridReady: (event: GridReadyEvent) => {
        if (event && this.lowerGridOpt.api) {
          this.lowerGridOpt.rowSelection = 'multiple';
          this.lowerGridOpt.api.setRowData([]);
          this.lowerGridOpt.api.sizeColumnsToFit();
        }
      },
      onFilterChanged: ({ api }: FilterChangedEvent) =>
        this.onFilterChangedGrid(api, ElectronicGridEnum.LowerGridOpt),
      onSelectionChanged: ({ api }: SelectionChangedEvent) =>
        this.onSelectionChangedGrid(api, ElectronicGridEnum.LowerGridOpt),
      onSortChanged: ({ api }: SortChangedEvent) =>
        this.updateClearAndFilterBtn(api, ElectronicGridEnum.LowerGridOpt),
    };

    this.denialReasonsGrid = <GridOptions>{
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      columnDefs: [
        { headerName: 'GRP/CARC', field: 'code' },
        {
          headerName: 'CARC-AMT',
          field: 'amount',
          cellStyle: { 'text-align': 'end' },
          cellRenderer: (params) => _currencyFormat(params.data.amount),
        },
        { headerName: 'Description', field: 'description' },
      ],
      onGridReady: (): void => this.subscribeToDenialReasons(),
    };
  }

  subscribeToDenialReasons(): void {
    this.subscriptions.push(
      this.objServ.behaviorSubjDepositObs$.subscribe({
        next: (resp: { action: string; data: any }): void => {
          if (!resp) {
            return;
          }
          const { action, data }: { action: string; data: any } = resp;
          if (action === DepositAction.ShowDenialReasonsAfterDoubleClick) {
            this.denialReasonsGrid.api.setRowData(data);
            this.denialReasonsGrid.api.sizeColumnsToFit();
          }
        },
      })
    );
  }

  getClaimDetailById() {
    this.claimDetailGrid.api.setRowData([]);
    if (
      this.lowerGridOpt &&
      this.lowerGridOpt.api &&
      this.lowerGridOpt.api.getSelectedRows().length
    ) {
      const selectedRows = this.lowerGridOpt.api.getSelectedRows();
      const first = selectedRows.find((f) => f.electronicid);
      const result = {
        electronicid: first ? first.electronicid : null,
        icn: selectedRows.map((row) => row.icn),
      };
      this.subscriptions.push(
        this.objServ.getClaimDetailById(result).subscribe({
          next: (value) => {
            this.claimDetailGrid.api.setRowData(value);
            this.claimDetailGrid.api.sizeColumnsToFit();
            this.totalRowsClaimsGrid = value.length;
            this.totalBilledDetail = this.calculateTotalAmount(
              value,
              'billedamount'
            );
            this.totalAllowedDetail = this.calculateTotalAmount(
              value,
              'totalallowedamount'
            );
            this.totalDeductDetail = this.calculateTotalAmount(
              value,
              'deductibleamount'
            );
            this.totalCoinsDetail = this.calculateTotalAmount(
              value,
              'coinsuranceamount'
            );
            this.totalPaidDetail = this.calculateTotalAmount(
              value,
              'paidamount'
            );
            this.checkDetail = value;
            this.checkDetail.map((item: { checkId: number }) => {
              item.checkId = this.checkId;
            });
            this.lastCheckDetail = this.checkDetail.pop();
            const firstPosition = 0;
            this.firstDetail = this.checkDetail.at(firstPosition);
          },
          error: (err) => {
            this.sweetAlertHelper.captureException(err);
          },
        })
      );
    }
  }

  onFilterChangedGrid(gridApi: GridApi, gridKey: ElectronicGridEnum): void {
    this.updateClearAndFilterBtn(gridApi, gridKey);

    const onFilterChangedInUpperGridOpt = () => {
      const rows: RowNode[] = gridApi.getRenderedNodes();

      if (!rows.length) {
        this.totalAmountSelected = 0;
        return;
      }

      this.totalAmountSelected = this.calculateTotalAmount(
        rows.map(({ data }) => data.electroniclist),
        'checkeft'
      );
    };

    const onFilterChangedInClaimDetailGrid = () => {
      const rows: RowNode[] = gridApi.getRenderedNodes();

      if (!rows.length) {
        this.totalBilledDetail = 0;
        this.totalAllowedDetail = 0;
        this.totalDeductDetail = 0;
        this.totalCoinsDetail = 0;
        this.totalPaidDetail = 0;
        return;
      }

      const details = rows.map(({ data }) => data);

      this.totalBilledDetail = this.calculateTotalAmount(
        details,
        'billedamount'
      );
      this.totalAllowedDetail = this.calculateTotalAmount(
        details,
        'totalallowedamount'
      );
      this.totalDeductDetail = this.calculateTotalAmount(
        details,
        'deductibleamount'
      );
      this.totalCoinsDetail = this.calculateTotalAmount(
        details,
        'coinsuranceamount'
      );
      this.totalPaidDetail = this.calculateTotalAmount(details, 'paidamount');
    };
  }

  updateClearAndFilterBtn(gridApi: GridApi, gridKey: ElectronicGridEnum) {
    const disableClearFilterBtn: boolean = !Object.keys(
      gridApi.getFilterModel()
    ).length;
    const disableClearSortBtn: boolean = !gridApi.getSortModel().length;

    switch (gridKey) {
      case ElectronicGridEnum.UpperGridOpt:
        this.disableClearFilterUpperGridOpt = disableClearFilterBtn;
        this.disableClearSortUpperGridOpt = disableClearSortBtn;
        break;
      case ElectronicGridEnum.ClaimDetailGrid:
        this.disableClearFilterClaimDetailGrid = disableClearFilterBtn;
        this.disableClearSortClaimDetailGrid = disableClearSortBtn;
        break;
      case ElectronicGridEnum.LowerGridOpt:
        this.disableClearFilterLowerGridOpt = disableClearFilterBtn;
        this.disableClearSortLowerGridOpt = disableClearSortBtn;
        break;
    }
  }

  onSelectionChangedGrid(gridApi: GridApi, gridKey: ElectronicGridEnum): void {
    const onLowerGridOptSelectionChanged = () => {
      const selectedRows = gridApi.getSelectedRows();

      if (!selectedRows.length) {
        this.selectedLowerGridOptRows = 0;
        return;
      }

      this.selectedLowerGridOptRows = selectedRows.length;
    };

    switch (gridKey) {
      case ElectronicGridEnum.UpperGridOpt:
        break;
      case ElectronicGridEnum.ClaimDetailGrid:
        break;
      case ElectronicGridEnum.LowerGridOpt:
        onLowerGridOptSelectionChanged();
        break;
    }
  }

  renderAndGetterValuesForDenialReasons(params: ValueGetterParams) {
    const data = params.data.denialreasons as IDenialReason[];
    return data.length ? data.map((el) => el.code).join(', ') : '';
  }

  toggleTab(tabNumber: number): boolean {
    return this.activatedTab === tabNumber;
  }

  clearFilterAndSort(
    AgButtonAction: AgButtonsEnum,
    gridKey: ElectronicGridEnum
  ) {
    const gridApiByElectronicGridEnum = {
      [ElectronicGridEnum.ClaimDetailGrid]: this.claimDetailGrid.api,
      [ElectronicGridEnum.LowerGridOpt]: this.lowerGridOpt.api,
    };

    const gridApi: GridApi = gridApiByElectronicGridEnum[gridKey];

    switch (AgButtonAction) {
      case AgButtonsEnum.RemoveFilter:
        gridApi.setFilterModel(null);
        break;
      case AgButtonsEnum.RemoveSort:
        gridApi.setSortModel(null);
        break;
    }

    this.updateClearAndFilterBtn(gridApi, gridKey);
  }

  calculateTotalAmount = (res: any[], key: string): number =>
    res.reduce((sum, item) => sum + (item[key] ? item[key] : 0), 0);

  get totListFooter(): { totalLabel: string; totalAmountSelected: number }[] {
    return [
      {
        totalLabel: 'Total Billed Amt.:',
        totalAmountSelected: this.totalBilledDetails,
      },
      {
        totalLabel: 'Total Paid Amt.:',
        totalAmountSelected: this.totaPaidDetails,
      },
    ];
  }

  get totListFooterDetail(): {
    totalLabel: string;
    totalAmountSelected: number;
  }[] {
    return [
      {
        totalLabel: 'Total Billed Amt.:',
        totalAmountSelected: this.totalBilledDetail,
      },
      {
        totalLabel: 'Total Allowed Amt.:',
        totalAmountSelected: this.totalAllowedDetail,
      },
      {
        totalLabel: 'Total Deduct Amt.:',
        totalAmountSelected: this.totalDeductDetail,
      },
      {
        totalLabel: 'Total Coins Amt.:',
        totalAmountSelected: this.totalCoinsDetail,
      },
      {
        totalLabel: 'Total Paid Amt.:',
        totalAmountSelected: this.totalPaidDetail,
      },
    ];
  }

  get denialReasonsModalHeaderText(): string {
    return `Denial Reasons | Procedure: ${this.selectedProceduteClaimDetail} | ICN: ${this.selectedIcnClaimDetail}`;
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/billing-general/deposits/electronic-details/electronic-details.component.ts